import {
    AfterContentChecked,
    AfterViewInit,
    Component,
    effect,
    inject,
    Input,
    input,
    OnInit,
    signal
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { CompanyEntity } from "@arch/domain/entity";
import {
    SerieAccountsPlanRowsType,
    SerieBatchEntity
} from "@arch/domain/entity/serie-batch.entity";
import { GetSeriesAccountsPlanController } from "@arch/presentation/controllers";
import { VisualizationOptionsType } from "@arch/_shared/types/options-visualization";
import { DateUtils } from "@arch/_shared/utils";
import { GetPointsRepositoryController } from "@arch/presentation/controllers/get-points-repository.controller";
import { DeviceService } from "@app/shared/services/device.service";

@Component({
    selector: "app-table-projection",
    imports: [CommonModule, SharedMaterialModule, SharedDsnModule],
    templateUrl: "./table-projection.component.html",
    styleUrl: "./table-projection.component.scss"
})
export class TableProjectionComponent
    implements AfterViewInit, OnInit, AfterContentChecked
{
    private readonly deviceService = inject(DeviceService);
    public readonly isMobile = this.deviceService.getIsMobile();
    public loading = input<boolean>(false);
    public options = input<VisualizationOptionsType>(null);
    public pdfMode = input<boolean>(false);
    @Input({ required: false }) dates: Date[];
    @Input({ required: false }) indexInterval: number[];
    @Input({ required: true }) company: CompanyEntity;
    private readonly getPointsRepositoryController = inject(
        GetPointsRepositoryController
    );
    private readonly getSeriesAccountsPlanController = inject(
        GetSeriesAccountsPlanController
    );

    public months: string[] = [];

    constructor() {
        effect(() => {
            if (!this.loading()) {
                const series = this.getSeriesAccountsPlanController.execute(
                    this.options()?.source,
                    this.company.props._id
                );
                const input = {
                    series: series,
                    scenarioId: this.options()?.scenario.props._id,
                    period: this.options()?.period,
                    range: {
                        startDate: this.options()?.startDate,
                        endDate: this.options()?.endDate
                    }
                };
                const points =
                    this.getPointsRepositoryController.execute(input);

                const seriesProjection = SerieBatchEntity.create(series);
                const dates = this.getDates();
                this.months = dates.map((d) => d.format);
                const rows = seriesProjection.getProjectionRowsTable({
                    months: dates,
                    points
                });

                if (this.pdfMode()) {
                    seriesProjection.expandAll(rows);
                }

                if (this.indexInterval) {
                    this.projectionItems.set(
                        rows.filter((data, index) =>
                            this.indexInterval.includes(index)
                        )
                    );
                } else {
                    this.projectionItems.set(rows);
                }
            }
        });
    }

    ngAfterViewInit(): void {}

    ngAfterContentChecked(): void {}

    public projectionItems = signal<SerieAccountsPlanRowsType[]>([]);

    ngOnInit(): void {}

    private getDates(): { format: string; date: Date }[] {
        if (this.dates) {
            return this.dates.map((d) => ({
                format: DateUtils.formatByPeriodName(
                    this.options()?.period,
                    d,
                    true
                ),
                date: d
            }));
        }
        const dates = DateUtils.getDatesInterval(
            this.options()?.startDate,
            this.options()?.endDate,
            this.options()?.period
        );

        const months = dates.map((d) => ({
            format: DateUtils.formatByPeriodName(
                this.options()?.period,
                d,
                true
            ),
            date: d
        }));

        return months;
    }

    toggleExpansion(item: SerieAccountsPlanRowsType, event: Event): void {
        if (!item.metadata.hasChildren || this.pdfMode()) return;

        const items = this.projectionItems();
        const itemIndex = items.findIndex(
            (i) => i.serie.props._id === item.serie.props._id
        );

        items[itemIndex].metadata.isExpanded =
            !items[itemIndex].metadata.isExpanded;

        this.updateChildrenVisibility(
            item.serie.props._id,
            items[itemIndex].metadata.isExpanded
        );

        this.projectionItems.set([...items]);
    }

    private updateChildrenVisibility(
        parentId: string,
        isVisible: boolean
    ): void {
        const items = this.projectionItems();

        items.forEach((item) => {
            if (item.metadata.parentId === parentId) {
                item.metadata.isVisible = isVisible;

                if (!isVisible || !item.metadata.isExpanded) {
                    this.updateChildrenVisibility(item.serie.props._id, false);
                } else {
                    this.updateChildrenVisibility(
                        item.serie.props._id,
                        item.metadata.isExpanded
                    );
                }
            }
        });
    }

    get visibleItems(): SerieAccountsPlanRowsType[] {
        return this.projectionItems().filter((item) => item.metadata.isVisible);
    }

    formatCurrency(value: number): string {
        return value.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    getRowClass(index: number): string {
        const item = this.visibleItems[index];
        if (!item) return "";

        switch (item.metadata.level) {
            case 0:
                return "c-table-budget__row--level-0";
            case 1:
                return "c-table-budget__row--level-1";
            case 2:
                return "c-table-budget__row--level-2";
            case 3:
            case 4:
                return "";
            default:
                return "";
        }
    }

    getIndentStyle(level: number): { [key: string]: string } {
        switch (level) {
            case 0:
            case 1:
                return {
                    "padding-left": "24px"
                };
            case 2:
                return {
                    "padding-left": "24px"
                };
            case 3:
                return {
                    "padding-left": "46px"
                };
            case 4:
                return {
                    "padding-left": "58px"
                };
            case 5:
                return {
                    "padding-left": "70px"
                };
            default:
                return {
                    "padding-left": `${level * 16}px`
                };
        }
    }

    public getLastChildByMainParent(item: SerieAccountsPlanRowsType): boolean {
        const parents = this.projectionItems().filter(
            (p) => p.metadata.parentMain === item.metadata.parentMain
        );

        return (
            parents[parents.length - 1].serie.props._id === item.serie.props._id
        );
    }

    public getLastCompoundChild(item: SerieAccountsPlanRowsType): boolean {
        const serieParentMain = this.projectionItems().find(
            (i) => i?.serie?.props._id === item?.metadata?.parentId
        );

        const seriesId = serieParentMain?.serie?.props?.compound;
        if (!seriesId) return false;
        const seriesCompound = this.projectionItems().filter((s) =>
            seriesId.includes(s.serie.props._id)
        );
        return (
            seriesCompound[seriesCompound.length - 1].serie.props._id ===
            item.serie.props._id
        );
    }

    public isGroup(item: SerieAccountsPlanRowsType): boolean {
        const groupindex = this.projectionItems().findIndex(
            (i) => i.serie.props._id === item.serie.props._id
        );
        const itemIsChildren = this.projectionItems().slice(
            groupindex,
            this.projectionItems().length
        )?.[1]?.metadata?.hasChildren;
        return !itemIsChildren;
    }

    public getBeforeParent(item: SerieAccountsPlanRowsType): boolean {
        const items = this.projectionItems();
        const index = items.findIndex(
            (i) => i.serie.props._id === item.serie.props._id
        );
        const serieBefore = items[index - 1];
        return items[index].serie.props._id === serieBefore.metadata.parentMain;
    }

    trackByItemId(index: number, item: SerieAccountsPlanRowsType): string {
        return item.serie.props._id;
    }

    public isCompoundChildren(item: SerieAccountsPlanRowsType): boolean {
        if (item.metadata?.parentMain) return false;
        const serieParentMain = this.projectionItems().find(
            (i) => i?.serie?.props._id === item?.metadata?.parentId
        );
        if (!serieParentMain) return false;
        const seriesId = serieParentMain?.serie?.props?.compound;
        const seriesCompound = this.projectionItems().filter(
            (s) =>
                seriesId.includes(s.serie.props._id) &&
                s.serie?.props?.compound?.length
        );
        if (seriesCompound.length) return false;
        if (!seriesId) return false;
        return seriesId.includes(item.serie.props._id);
    }

    public isFromParentMain(item: SerieAccountsPlanRowsType): boolean {
        const projectionItems = this.projectionItems();
        const parentIndex = projectionItems.findIndex(
            (i) => i.serie.props._id === item.serie.props._id
        );

        if (item.metadata.level <= 1) return false;
        if (item.metadata.hasChildren) return true;
        if (
            parentIndex < 0 ||
            !projectionItems?.[parentIndex]?.metadata?.parentMain
        )
            return false;
        return (
            projectionItems?.[parentIndex]?.serie.props._id ===
            projectionItems?.[parentIndex + 1]?.metadata.parentMain
        );
    }

    public isFromParentCoumpound(item: SerieAccountsPlanRowsType): boolean {
        const projectionItems = this.projectionItems();
        const parentIndex = projectionItems.findIndex(
            (i) => i.serie.props._id === item.serie.props._id
        );

        if (item.metadata.level < 1) return false;
        if (item.metadata.hasChildren) return true;
        if (
            parentIndex < 0 ||
            !projectionItems?.[parentIndex]?.metadata?.parentMain
        )
            return false;
        return (
            projectionItems?.[parentIndex]?.serie.props._id ===
            projectionItems?.[parentIndex + 1]?.metadata.parentId
        );
    }
}
